import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { curationApi } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Users, BookOpen, Copy, Plus, Loader2, Rss } from 'lucide-react'
import { motion } from 'framer-motion'

export function CurationPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedUserFeeds, setSelectedUserFeeds] = useState([])
  const [loadingFeeds, setLoadingFeeds] = useState(false)

  const { data: curatedUsers, isLoading } = useQuery({
    queryKey: ['curatedUsers'],
    queryFn: async () => {
      const response = await curationApi.getCuratedUsers()
      return response.data.curated_users
    },
  })

  const cloneMutation = useMutation({
    mutationFn: (sourceUserAlias) => curationApi.cloneFeed(sourceUserAlias),
    onSuccess: (data) => {
      toast.success(`Feed cloned successfully! ${data.data.feed_count} feeds replaced.`)
      queryClient.invalidateQueries(['userProfile'])
      queryClient.invalidateQueries(['subscribedFeeds'])
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.msg || 'Failed to clone feed. Please try again.'
      toast.error(errorMsg)
    },
  })

  const addMutation = useMutation({
    mutationFn: (sourceUserAlias) => curationApi.addFeed(sourceUserAlias),
    onSuccess: (data) => {
      toast.success(`Feeds added successfully! ${data.data.feed_count} feeds added to your feed.`)
      queryClient.invalidateQueries(['userProfile'])
      queryClient.invalidateQueries(['subscribedFeeds'])
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.msg || 'Failed to add feeds. Please try again.'
      toast.error(errorMsg)
    },
  })

  const fetchUserFeeds = async (user) => {
    setLoadingFeeds(true)
    try {
      const response = await curationApi.getCuratedUserFeeds(user.alias)
      setSelectedUserFeeds(response.data.feeds || [])
    } catch (error) {
      toast.error('Failed to load feeds')
      setSelectedUserFeeds([])
    } finally {
      setLoadingFeeds(false)
    }
  }

  const handleRead = (user) => {
    // Encode user alias in base64 and navigate to dedicated curation read page
    const curationParam = btoa(user.alias)
    navigate(`/curation/${curationParam}`)
  }

  const handleCloneClick = async (user) => {
    setSelectedUser(user)
    await fetchUserFeeds(user)
    setShowCloneModal(true)
  }

  const handleAddClick = async (user) => {
    setSelectedUser(user)
    await fetchUserFeeds(user)
    setShowAddModal(true)
  }

  const handleClone = () => {
    if (selectedUser) {
      cloneMutation.mutate(selectedUser.alias)
      setShowCloneModal(false)
      navigate('/')
    }
  }

  const handleCloneAndEdit = () => {
    if (selectedUser) {
      cloneMutation.mutate(selectedUser.alias)
      setShowCloneModal(false)
      navigate('/dashboard/feed')
    }
  }

  const handleAdd = () => {
    if (selectedUser) {
      addMutation.mutate(selectedUser.alias)
      setShowAddModal(false)
      navigate('/')
    }
  }

  const handleAddAndEdit = () => {
    if (selectedUser) {
      addMutation.mutate(selectedUser.alias)
      setShowAddModal(false)
      navigate('/dashboard/feed')
    }
  }

  const getUserInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <Users className="h-8 w-8 text-primary" />
          Feed Curation
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Clone feeds from well-known journalists and curators. Get started with a professionally curated feed in one click.
        </p>
      </div>

      {!curatedUsers || curatedUsers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Curated Feeds Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Check back soon for professionally curated feeds from journalists and experts.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {curatedUsers.map((user, index) => (
            <motion.div
              key={user.alias}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Profile Image */}
                    <Avatar className="h-20 w-20 flex-shrink-0">
                      {user.profile_picture && (
                        <AvatarImage src={user.profile_picture} alt={user.name} />
                      )}
                      <AvatarFallback className="bg-gray-800 text-white text-xl">
                        {getUserInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    {/* Name - with more space */}
                    <div className="flex-1 min-w-0 mr-4">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                        {user.name}
                      </h3>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        onClick={() => handleRead(user)}
                        className="w-28 bg-gray-800 hover:bg-gray-900 text-white"
                        size="sm"
                        title="Read this curated feed"
                      >
                        Read
                      </Button>
                      
                      <Button
                        onClick={() => handleCloneClick(user)}
                        className="w-28 bg-gray-800 hover:bg-gray-900 text-white"
                        size="sm"
                        title="Clone this curated feed. This replaces your current feed with the publications in this feed."
                      >
                        Clone
                      </Button>
                      
                      <Button
                        onClick={() => handleAddClick(user)}
                        className="w-28 bg-gray-800 hover:bg-gray-900 text-white"
                        size="sm"
                        title="Add this curated feed to you. Any publication in this feed not currently in yours will be added to it."
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Clone Confirmation Modal */}
      <Dialog open={showCloneModal} onOpenChange={setShowCloneModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Clone Feed</DialogTitle>
            <DialogDescription className="pt-2 text-base">
              This replaces your current feed with the publications in this curated feed. You cannot undo this action.
            </DialogDescription>
          </DialogHeader>
          
          {loadingFeeds ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm font-bold mb-3 px-1">{selectedUserFeeds.length} publications will be cloned:</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {selectedUserFeeds.map((feed) => (
                  <div key={feed.alias} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    {feed.icon ? (
                      <img src={feed.icon} alt={feed.display_name} className="h-10 w-10 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{feed.display_name || feed.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCloneModal(false)}
              className="bg-green-500 hover:bg-green-600 text-white border-0"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClone}
              disabled={cloneMutation.isPending}
            >
              {cloneMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cloning...
                </>
              ) : (
                'Clone'
              )}
            </Button>
            <Button
              onClick={handleCloneAndEdit}
              disabled={cloneMutation.isPending}
            >
              {cloneMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cloning...
                </>
              ) : (
                'Clone & Edit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Confirmation Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add Feed</DialogTitle>
            <DialogDescription className="pt-2 text-base">
              This takes your current feed and adds to it the publication in this curated feed. You cannot undo this action.
            </DialogDescription>
          </DialogHeader>
          
          {loadingFeeds ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm font-bold mb-3 px-1">{selectedUserFeeds.length} publications will be added:</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {selectedUserFeeds.map((feed) => (
                  <div key={feed.alias} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    {feed.icon ? (
                      <img src={feed.icon} alt={feed.display_name} className="h-10 w-10 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{feed.display_name || feed.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="bg-green-500 hover:bg-green-600 text-white border-0"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add'
              )}
            </Button>
            <Button
              onClick={handleAddAndEdit}
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add & Edit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
